import { createPortal } from "react-dom";
import { useEffect, useRef, useState } from "react";
import { LocalDate } from "@js-joda/core";
import { useTranslation } from "react-i18next";
import { DatePicker } from "components/designV2/datePicker/datePicker";
import {
  updateCompanyAnalyticsSettings,
  loadWithLegacyFallback,
} from "services/firebase/firebase.service";
import {
  type AnalyticsPeriod,
  getWeekBounds,
  isDateInPeriod,
} from "./analytics.operatingHours";
import {
  Backdrop,
  Pane,
  PaneHeader,
  PaneTitle,
  CloseBtn,
  PaneDescription,
  PaneFooter,
  SaveErrorText,
  EmptyNote,
  DeleteBtn,
  AddSection,
  AddSectionTitle,
  FormField,
  FormLabel,
  FormInput,
  FormRow,
  DatePickerWrapper,
  AddButtonRow,
  CostList,
  CostRow,
  CostInfo,
  CostTitle,
  CostMeta,
  KindToggleRow,
  KindBtn,
  PeriodSelect,
} from "./analytics.customCosts.styles";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CustomCost {
  id: string;
  title: string;
  amount: number;
  /** "period" = recurring cost; "date" = one-off on a specific calendar day */
  kind: "period" | "date";
  /** Present when kind === "period" */
  period?: "day" | "week" | "month";
  /** ISO "YYYY-MM-DD", present when kind === "date" */
  date?: string;
}

// ─── Storage ──────────────────────────────────────────────────────────────────
//
// Lives in Firebase Storage under Company/{companyId}/analytics/custom-costs
// — the same "realm" as the company photo, just its own subfolder. The legacy
// localStorage key below is read-only now: consulted only as a one-time
// migration source for companies with costs saved before this moved
// off-device, then never written to again.

const LEGACY_STORAGE_KEY = "analytics_custom_costs";
const ANALYTICS_SETTINGS_KEY = "custom-costs";

const isCustomCost = (value: unknown): value is CustomCost => {
  if (typeof value !== "object" || value === null) return false;
  const c = value as Record<string, unknown>;
  return (
    typeof c.id === "string" &&
    typeof c.title === "string" &&
    typeof c.amount === "number" &&
    (c.kind === "period" || c.kind === "date") &&
    (c.period === undefined || typeof c.period === "string") &&
    (c.date === undefined || typeof c.date === "string")
  );
};

/** Type guard for the Firebase-stored value — see getCompanyAnalyticsSettings. */
const isCustomCostArray = (value: unknown): value is CustomCost[] =>
  Array.isArray(value) && value.every(isCustomCost);

const parseStoredCustomCosts = (raw: string | null): CustomCost[] | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      // Back-compat: old entries have period but no kind
      return parsed.map(
        (c): CustomCost => ({
          id: c.id,
          title: c.title,
          amount: c.amount,
          kind: c.kind === "date" ? "date" : "period",
          period: c.period,
          date: c.date,
        }),
      );
    }
  } catch {}
  return null;
};

export const loadCustomCosts = async (
  companyId: number,
): Promise<CustomCost[]> => {
  const costs = await loadWithLegacyFallback(
    companyId,
    ANALYTICS_SETTINGS_KEY,
    LEGACY_STORAGE_KEY,
    isCustomCostArray,
    parseStoredCustomCosts,
  );
  return costs ?? [];
};

export const saveCustomCosts = (
  companyId: number,
  costs: CustomCost[],
): Promise<void> =>
  updateCompanyAnalyticsSettings(companyId, ANALYTICS_SETTINGS_KEY, costs);

// ─── Period-aware total ───────────────────────────────────────────────────────

/**
 * Returns the effective total custom cost for the selected analytics period.
 *
 * For "period" costs:
 *  ┌──────────┬────────────────────┬──────────────────────┬──────────────────────┬────────────────────────┐
 *  │ cost     │ Day view           │ Week view            │ Month view           │ Year view              │
 *  ├──────────┼────────────────────┼──────────────────────┼──────────────────────┼────────────────────────┤
 *  │ day      │ amount             │ amount × 7           │ amount × daysInMonth │ amount × daysInYear    │
 *  │ week     │ amount / 7         │ amount               │ amount × days / 7    │ amount × days / 7      │
 *  │ month    │ amount / daysInMo  │ amount × 7 / days    │ amount               │ amount × 12            │
 *  └──────────┴────────────────────┴──────────────────────┴──────────────────────┴────────────────────────┘
 *
 * For "date" costs — filtered to the analytics window (day: exact match; week:
 * Mon–Sun; month: same calendar month; year: same calendar year).
 */
export const getCustomCostsTotal = (
  costs: CustomCost[],
  period: AnalyticsPeriod,
  selectedDate: LocalDate,
): number => {
  const daysInMonth = selectedDate.lengthOfMonth();
  // dayOfYear() on Dec 31 gives 365 or 366
  const daysInYear = LocalDate.of(selectedDate.year(), 12, 31).dayOfYear();

  return costs.reduce((sum, cost) => {
    const kind = cost.kind ?? "period";

    if (kind === "date") {
      if (!cost.date) return sum;
      const d = LocalDate.parse(cost.date);
      if (period === "day") {
        return cost.date === selectedDate.toString() ? sum + cost.amount : sum;
      }
      if (period === "week") {
        const { weekStart, weekEnd } = getWeekBounds(selectedDate);
        return !d.isBefore(weekStart) && !d.isAfter(weekEnd)
          ? sum + cost.amount
          : sum;
      }
      if (period === "month") {
        return d.year() === selectedDate.year() &&
          d.monthValue() === selectedDate.monthValue()
          ? sum + cost.amount
          : sum;
      }
      // year
      return d.year() === selectedDate.year() ? sum + cost.amount : sum;
    }

    // period cost — convert the stored recurring amount to the analytics view's timespan
    if (period === "day") {
      if (cost.period === "day") return sum + cost.amount;
      if (cost.period === "week") return sum + cost.amount / 7;
      return sum + cost.amount / daysInMonth; // monthly → daily
    }
    if (period === "week") {
      if (cost.period === "day") return sum + cost.amount * 7;
      if (cost.period === "week") return sum + cost.amount;
      return sum + (cost.amount * 7) / daysInMonth; // monthly → weekly
    }
    if (period === "month") {
      if (cost.period === "day") return sum + cost.amount * daysInMonth;
      if (cost.period === "week") return sum + (cost.amount * daysInMonth) / 7;
      return sum + cost.amount; // monthly = full amount
    }
    // year
    if (cost.period === "day") return sum + cost.amount * daysInYear;
    if (cost.period === "week") return sum + (cost.amount * daysInYear) / 7;
    return sum + cost.amount * 12; // monthly × 12
  }, 0);
};

// isDateInPeriod (used just below, for one-off "date" costs) now lives in
// analytics.operatingHours.tsx, shared with analytics.customSales.tsx.

// ─── Side pane ────────────────────────────────────────────────────────────────

interface CustomCostsPaneProps {
  initial: CustomCost[];
  open: boolean;
  companyId: number;
  /** Scopes the visible list to the selected analytics period — one-off
   * ("date") costs outside it are hidden; recurring ("period") costs always
   * show, since they aren't tied to a specific date. */
  period: AnalyticsPeriod;
  /** Pre-fills the date picker when adding a one-time cost */
  selectedDate: LocalDate;
  onSave: (costs: CustomCost[]) => void;
  onClose: () => void;
}

const generateId = (): string =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

export const CustomCostsPane = (props: CustomCostsPaneProps) => {
  const { t } = useTranslation();
  const [costs, setCosts] = useState<CustomCost[]>(props.initial);
  const [newTitle, setNewTitle] = useState("");
  const [newAmount, setNewAmount] = useState("");
  const [newKind, setNewKind] = useState<"period" | "date">("period");
  const [newPeriod, setNewPeriod] = useState<"day" | "week" | "month">("day");
  const [newDate, setNewDate] = useState<LocalDate>(props.selectedDate);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);

  // Reset local state on the false→true transition of props.open.
  // Using a ref to track the previous open value means we can list all
  // consumed props as deps without triggering a reset while the pane is
  // already open (e.g. parent re-renders with a new selectedDate mid-session).
  const wasOpen = useRef(false);
  useEffect(() => {
    if (props.open && !wasOpen.current) {
      setCosts(props.initial);
      setNewTitle("");
      setNewAmount("");
      setNewKind("period");
      setNewPeriod("day");
      setNewDate(props.selectedDate);
      setSaveError(false);
    }
    wasOpen.current = props.open;
  }, [props.open, props.initial, props.selectedDate]);

  const handleAdd = () => {
    const amount = parseFloat(newAmount.replace(",", "."));
    if (!newTitle.trim() || !Number.isFinite(amount) || amount <= 0) return;
    setCosts((prev) => [
      ...prev,
      newKind === "date"
        ? {
            id: generateId(),
            title: newTitle.trim(),
            amount,
            kind: "date",
            date: newDate.toString(),
          }
        : {
            id: generateId(),
            title: newTitle.trim(),
            amount,
            kind: "period",
            period: newPeriod,
          },
    ]);
    setNewTitle("");
    setNewAmount("");
    // keep kind/period/date — user likely adds a sequence of similar costs
  };

  const handleDelete = (id: string) =>
    setCosts((prev) => prev.filter((c) => c.id !== id));

  // The list only shows costs relevant to the currently selected period — a
  // one-off cost dated in August shouldn't clutter the list while February is
  // selected. Recurring costs always show since they aren't date-specific.
  // Deleting/saving still operates on the full `costs` array, so nothing
  // outside view is lost.
  const visibleCosts = costs.filter((cost) => {
    const kind = cost.kind ?? "period";
    if (kind === "period") return true;
    return (
      !!cost.date && isDateInPeriod(cost.date, props.period, props.selectedDate)
    );
  });

  const handleSave = () => {
    setIsSaving(true);
    setSaveError(false);
    saveCustomCosts(props.companyId, costs)
      .then(() => {
        setIsSaving(false);
        props.onSave(costs);
      })
      .catch(() => {
        setIsSaving(false);
        setSaveError(true);
      });
  };

  return createPortal(
    <>
      <Backdrop $open={props.open} onClick={props.onClose} />
      <Pane $open={props.open}>
        <PaneHeader>
          <PaneTitle>{t("CompanyAnalytics.CustomCosts")}</PaneTitle>
          <CloseBtn onClick={props.onClose} aria-label="Close">
            ✕
          </CloseBtn>
        </PaneHeader>
        <PaneDescription>
          {t("CompanyAnalytics.CustomCostsDescription")}
        </PaneDescription>

        {/* ── Existing costs list (scoped to the selected period) ── */}
        <CostList>
          {visibleCosts.length === 0 ? (
            <EmptyNote>{t("CompanyAnalytics.NoCustomCosts")}</EmptyNote>
          ) : (
            visibleCosts.map((cost) => {
              const kind = cost.kind ?? "period";
              const meta =
                kind === "date" && cost.date
                  ? (() => {
                      const d = LocalDate.parse(cost.date);
                      return `${String(d.dayOfMonth()).padStart(2, "0")}/${String(d.monthValue()).padStart(2, "0")}/${d.year()}`;
                    })()
                  : cost.period === "day"
                    ? t("CompanyAnalytics.PerDay")
                    : cost.period === "week"
                      ? t("CompanyAnalytics.PerWeek")
                      : t("CompanyAnalytics.PerMonth");
              return (
                <CostRow key={cost.id}>
                  <CostInfo>
                    <CostTitle>{cost.title}</CostTitle>
                    <CostMeta>
                      {Math.round(cost.amount).toLocaleString("sv-SE")} kr
                      {" · "}
                      {meta}
                    </CostMeta>
                  </CostInfo>
                  <DeleteBtn
                    onClick={() => handleDelete(cost.id)}
                    aria-label={t("CompanyAnalytics.DeleteCost")}
                  >
                    ✕
                  </DeleteBtn>
                </CostRow>
              );
            })
          )}
        </CostList>

        {/* ── Add new cost form ── */}
        <AddSection>
          <AddSectionTitle>{t("CompanyAnalytics.AddCost")}</AddSectionTitle>
          <FormField>
            <FormLabel>{t("CompanyAnalytics.CostTitle")}</FormLabel>
            <FormInput
              type="text"
              value={newTitle}
              placeholder={t("CompanyAnalytics.CostTitlePlaceholder")}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            />
          </FormField>

          {/* Periodic / One-time toggle */}
          <KindToggleRow>
            <KindBtn
              $active={newKind === "period"}
              onClick={() => setNewKind("period")}
              type="button"
            >
              {t("CompanyAnalytics.CostTypePeriodic")}
            </KindBtn>
            <KindBtn
              $active={newKind === "date"}
              onClick={() => setNewKind("date")}
              type="button"
            >
              {t("CompanyAnalytics.CostTypeOneTime")}
            </KindBtn>
          </KindToggleRow>

          <FormRow>
            <FormField>
              <FormLabel>{t("CompanyAnalytics.CostAmount")}</FormLabel>
              <FormInput
                type="number"
                value={newAmount}
                placeholder="0"
                min="0"
                onChange={(e) => setNewAmount(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              />
            </FormField>
            {newKind === "period" ? (
              <FormField>
                <FormLabel>{t("CompanyAnalytics.CostPeriod")}</FormLabel>
                <PeriodSelect
                  value={newPeriod}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "day" || v === "week" || v === "month")
                      setNewPeriod(v);
                  }}
                >
                  <option value="day">{t("CompanyAnalytics.PerDay")}</option>
                  <option value="week">{t("CompanyAnalytics.PerWeek")}</option>
                  <option value="month">
                    {t("CompanyAnalytics.PerMonth")}
                  </option>
                </PeriodSelect>
              </FormField>
            ) : (
              <FormField>
                <FormLabel>{t("CompanyAnalytics.CostDate")}</FormLabel>
                <DatePickerWrapper>
                  <DatePicker
                    value={newDate}
                    onChange={(date) => {
                      if (date) setNewDate(date);
                    }}
                    size="compact"
                    dateFormat="DD/MM/YYYY"
                  />
                </DatePickerWrapper>
              </FormField>
            )}
          </FormRow>
          <AddButtonRow>
            <SecondaryButton
              label={t("CompanyAnalytics.AddCost")}
              onClick={handleAdd}
            />
          </AddButtonRow>
        </AddSection>

        <PaneFooter>
          {saveError && (
            <SaveErrorText>{t("CompanyAnalytics.SaveError")}</SaveErrorText>
          )}
          <SecondaryButton
            label={t("CompanyAnalytics.Cancel")}
            onClick={props.onClose}
            disabled={isSaving}
          />
          <PrimaryButton
            label={t("CompanyAnalytics.Save")}
            onClick={handleSave}
            disabled={isSaving}
            isLoading={isSaving}
          />
        </PaneFooter>
      </Pane>
    </>,
    document.body,
  );
};
